import React, { createRef, memo, useEffect, useRef } from 'react';
import { SrOnly, Text } from 'pcln-design-system';
import Cell from './Cell';
import getContextualAriaLabel from './getContextualAriaLabel';
import { endDateDelta, fullDateChanged, hoveredDateDelta, selectableDateDelta, startDateDelta } from '../utils/shouldDayUpdate';
function Day({
  ariaLabel,
  customDayProps,
  dayWithRoundedCorners,
  dayWrapperStyleOverride,
  didUseKeyNavigation,
  endDate,
  fullDate,
  highlightedDates,
  hoveredDate,
  isTouch,
  maxDate,
  maxSelectableDate,
  minDate,
  showHoveredDates,
  startDate,
  use2024DesignSeti,
  getCalendarLegendId,
  onDayClicked,
  onDayHovered,
  renderDayFn,
  setSelectedDayRef = () => {}
}) {
  const cellRef = createRef();
  const hasComponentInitialized = useRef(false);
  useEffect(() => {
    if (hasComponentInitialized.current && Number(fullDate) === Number(hoveredDate)) {
      cellRef.current.focus();
      setSelectedDayRef(cellRef.current);
    }
    hasComponentInitialized.current = true;
  }, [fullDate, hoveredDate, setSelectedDayRef]);
  if (!fullDate) {
    return React.createElement(Cell, {
      py: 2
    });
  }
  const currentDate = parseInt(fullDate);
  const isStartDate = startDate && fullDate === startDate;
  const isEndDate = endDate && fullDate === endDate;
  const isAfterStart = startDate && fullDate > startDate;
  const isBeforeEnd = endDate && currentDate < Number(endDate);
  const isHovered = hoveredDate && !endDate && fullDate <= hoveredDate;
  const isValid = fullDate <= maxDate && fullDate >= minDate && fullDate <= maxSelectableDate;
  const isSelected = isStartDate || isEndDate;
  const isGrouped = isAfterStart && (isBeforeEnd || isHovered);
  const autobotId = `DATEPICKER_DAY_${fullDate}${!isValid ? '_INVALID' : ''}${isSelected ? '_SELECTED' : ''}${isGrouped ? '_GROUPED' : ''}`;
  const day = fullDate.substring(6).replace(/^0/, '');
  const combinedCustomDayProps = {
    customDayProps,
    day,
    fullDate,
    highlightedDates,
    isGrouped,
    isSelected,
    isValid,
    use2024DesignSeti
  };
  const customDayComponent = typeof renderDayFn === 'function' ? renderDayFn(combinedCustomDayProps) : null;
  const isCustomDayAvailable = customDayComponent && React.isValidElement(customDayComponent);
  const price = combinedCustomDayProps?.customDayProps?.price;
  const calendarLegendId = getCalendarLegendId ? getCalendarLegendId({
    date: fullDate
  }) : null;
  const dayDescriptionId = `${fullDate}-calendar-day`;
  const ariaDescribedBy = `${calendarLegendId} ${dayDescriptionId}`;
  function onClick(evt) {
    if (isValid) {
      onDayClicked?.(evt, fullDate);
    }
  }
  function onMouseOver(evt) {
    if (isValid && showHoveredDates) {
      onDayHovered?.(evt, fullDate);
    }
  }
  const isHoveredDate = fullDate === hoveredDate;
  const isMinDate = !hoveredDate && fullDate === minDate;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    React.createElement("div", {
      "aria-describedby": ariaDescribedBy,
      "aria-pressed": isValid && isSelected || 'false',
      role: 'button',
      className: 'day-cell',
      tabIndex: isMinDate || isHoveredDate ? 0 : -1,
      onClick: onClick,
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events,
      onMouseOver: onMouseOver,
      ref: cellRef,
      style: dayWrapperStyleOverride
    }, React.createElement(Cell, {
      "data-autobot-element-id": autobotId,
      grouped: isGrouped,
      isCurrentHoverDate: didUseKeyNavigation && currentDate === Number(hoveredDate),
      isEnd: isEndDate,
      isStart: isStartDate,
      isTouch: isTouch,
      roundedCorners: dayWithRoundedCorners,
      selected: isSelected,
      showHoveredDates: showHoveredDates,
      use2024DesignSeti: use2024DesignSeti,
      valid: isValid,
      width: 1
    }, React.createElement(SrOnly, {
      id: `${fullDate}-calendar-day`
    }, getContextualAriaLabel({
      ariaLabel,
      endDate,
      isDateFocusable: isStartDate || isEndDate,
      price,
      startDate
    })), isCustomDayAvailable ? customDayComponent : React.createElement(Text, {
      fontWeight: use2024DesignSeti ? 'normal' : 'bold',
      fontSize: 2,
      py: 2
    }, day)))
  );
}
export function areEqual(prevProps, nextProps) {
  const {
    fullDate,
    startDate,
    endDate,
    hoveredDate,
    maxSelectableDate
  } = prevProps;
  const {
    startDate: nextStartDate,
    endDate: nextEndDate,
    hoveredDate: nextHoveredDate,
    maxSelectableDate: nextMaxSelectableDate
  } = nextProps;
  if (startDate !== nextStartDate) {
    return false;
  }
  if (endDate !== nextEndDate) {
    return false;
  }
  if (fullDateChanged(fullDate, nextProps.fullDate)) {
    return false;
  }
  if (!fullDate) {
    return true;
  }
  const inStartDateDelta = startDateDelta(startDate, nextStartDate, fullDate, nextEndDate);
  const inEndDateDelta = endDateDelta(endDate, nextEndDate, fullDate, nextStartDate);
  const inHoveredDateDelta = hoveredDateDelta(hoveredDate, nextHoveredDate, fullDate);
  const inSelectableDateDelta = selectableDateDelta(maxSelectableDate, nextMaxSelectableDate, fullDate);
  const hasNewCustomDayProps = prevProps.customDayProps !== nextProps.customDayProps;
  return !hasNewCustomDayProps && !inStartDateDelta && !inEndDateDelta && !inHoveredDateDelta && !inSelectableDateDelta;
}
export default memo(Day, areEqual);